import { useEffect, useRef, useState } from 'react'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import moment from 'moment'

import { ActualTimeWindowInterval, ETimeUnit, TimeWindowInterval } from '@types'

// Реэкспортируем для обратной совместимости
export type { ActualTimeWindowInterval, TimeWindowInterval }
export { ETimeUnit }

const DEFAULT_MS_OFFSET = 1000 * 60

export const useTimeWindowInterval = () => {
  const [schedulerValueMs, setSchedulerValueMs] = useState(DEFAULT_MS_OFFSET)

  // Изначальное окно времени. Точка отсчёта, от которой начинается интервал
  const [startTimeWindow, setStartTimeWindow] = useState<TimeWindowInterval>(null)

  const [timeUnit, setTimeUnit] = useState<ETimeUnit>(ETimeUnit.HOUR)

  const [field, setField] = useState<string>('')

  // Текущее окно времени, которое постоянно сдвигается в результате работы setInterval
  const [actualTimeWindow, setActualTimeWindow] = useState<ActualTimeWindowInterval>(null)

  // Добавляем флаг для отслеживания первого срабатывания
  const isFirstIntervalRunRef = useRef(true)
  // Храним setInterval в ref
  const timeWindowIntervalRef = useRef<NodeJS.Timer | null>(null)
  // Храним актуальное значение actualTimeWindow в ref для доступа внутри setInterval
  const actualTimeWindowRef = useRef<ActualTimeWindowInterval>(null)
  // Триггер для принудительного перезапуска интервала при одинаковых параметрах
  const [restartTrigger, setRestartTrigger] = useState(0)

  const updateTimeWindow = (
    value: TimeWindowInterval,
    field: string,
    ms?: number,
    timeUnit?: ETimeUnit
  ) => {
    // Очищаем существующий интервал перед обновлением
    if (timeWindowIntervalRef.current) {
      clearInterval(timeWindowIntervalRef.current as unknown as number)
      timeWindowIntervalRef.current = null
    }

    setStartTimeWindow(value)
    setField(field)
    setSchedulerValueMs(ms || DEFAULT_MS_OFFSET)
    setTimeUnit(timeUnit || ETimeUnit.HOUR)
    isFirstIntervalRunRef.current = true

    // Принудительно перезапускаем интервал при одинаковых параметрах
    setRestartTrigger(prev => prev + 1)
  }

  // Обновляем actualTimeWindow при изменении startTimeWindow или сбрасываем при его очистке
  useEffect(() => {
    if (startTimeWindow) {
      const newActualTimeWindow: ActualTimeWindowInterval = {
        interval: [startTimeWindow[0], startTimeWindow[1], startTimeWindow[2]],
        field,
        isFirstIntervalRun: isFirstIntervalRunRef.current,
        timeUnit,
      }
      setActualTimeWindow(newActualTimeWindow)
      actualTimeWindowRef.current = newActualTimeWindow
    } else {
      setActualTimeWindow(null)
      actualTimeWindowRef.current = null
    }
  }, [startTimeWindow, field, timeUnit])

  // Создаём и сбрасываем setInterval. Обрабатываем callback интервала
  useEffect(() => {
    if (!startTimeWindow) {
      return
    }

    timeWindowIntervalRef.current = setInterval(() => {
      // Начало следующего окна времени. Изначально берётся дефолтное окно - startTimeWindow
      const start = actualTimeWindowRef.current?.interval
        ? actualTimeWindowRef.current.interval[0]
        : startTimeWindow[0]
      // Конец следующего окна времени
      const end = moment(start).add(schedulerValueMs, 'ms').toISOString()

      if (isFirstIntervalRunRef.current) {
        isFirstIntervalRunRef.current = false
      }

      // Сдвигаем текущее окно времени
      const newActualTimeWindow: ActualTimeWindowInterval = {
        interval: [end, startTimeWindow[1], startTimeWindow[2]],
        field,
        isFirstIntervalRun: isFirstIntervalRunRef.current,
        timeUnit,
      }
      setActualTimeWindow(newActualTimeWindow)
      actualTimeWindowRef.current = newActualTimeWindow
    }, schedulerValueMs)

    return () => {
      clearInterval(timeWindowIntervalRef.current as unknown as number)
    }
  }, [startTimeWindow, schedulerValueMs, field, timeUnit, restartTrigger])

  const checkIsValidTimeWindowInterval = (value: unknown): value is TimeWindowInterval => {
    return isArray(value) && moment(value[0]).isValid() && !isNil(value[1]) && !isNil(value[2])
  }

  return { checkIsValidTimeWindowInterval, updateTimeWindow, actualTimeWindow }
}
